import React, { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Select from "react-select";
import { getValue } from "@testing-library/user-event/dist/utils";
import { getAllBrands } from "../../services/brand.service";
import { useSelector } from "react-redux";
import { getAllSizes } from "../../services/size.service";
import { getAllProducts } from "../../services/product.service";
import {
  addDesignRate,
  getAllDesignRates,
} from "../../services/ratediary.service";

export default function RateDiaryAdd() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    clearErrors,
  } = useForm();

  const [designRate, setDesignRate] = useState(null);
  const [barcodeDate, setBarcodeDate] = useState("");
  const [isRateEditable, setIsRateEditable] = useState(true);
  const [isLoading2, setIsLoading2] = useState(false);
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [products, setProducts] = useState([]);
  const [rateDiary, setRateDiary] = useState(null);
  const { token } = useSelector((state) => state?.auth);
  const [designNoList, setDesignNoList] = useState([]);
  const navigate = useNavigate();
  const designNo = watch("designNumber");
  const designSize = watch("designSize");
  const acronym = watch("acronym") || null;

  const handleDesignRateFocus = () => {
    if (!isRateEditable && designRate) {
      Swal.fire({
        title: "Design Rate",
        text: `The current design rate is ₹${designRate}, which was last updated on ${new Date(
          rateDiary?.design_rate_update_date
        )
          .toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })
          .replace(" ", " ")
          .replace(/(\w+ \w+)/, "$1,")}. Do you want to update it?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Update",
        cancelButtonText: "No, Keep Current",
      }).then((result) => {
        if (result.isConfirmed) {
          setIsRateEditable(true); // Allow editing
        } else {
          setValue("designRate", designRate); // Keep the existing rate
          setIsRateEditable(false); // Disable editing
        }
      });
    }
  };

  const fetchRateDiary = async (designId) => {
    try {
      const params = { design_id: designId };
      const res = await getAllDesignRates(token, params);

      if (res && res.status >= 200 && res.status < 300) {
        setRateDiary(res.data);
        setDesignRate(res.data.design_rate);
        setValue("designRate", res.data.design_rate);
        res?.data?.design_rate
          ? setIsRateEditable(false)
          : setIsRateEditable(true);

        console.log("Rate Diary fetched successfully:", res.data);
      } else {
        setDesignRate(null); // Reset design rate on failure
        setValue("designRate", null);
      }
    } catch (error) {
      console.error("Error fetching Rate Diary:", error);
    }
  };

  const fetchBrands = async (page = false) => {
    try {
      const params = { page };
      const res = await getAllBrands(token, params);
      if (res && res.status >= 200 && res.status < 300) {
        setBrands(res?.data);
        console.log("Brands fetched successfully:", res.data);
      } else {
        Swal.fire(
          "Error",
          res?.data?.message || "Failed to fetch Brands. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching Brands:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  const fetchProducts = async (brandId, page = false) => {
    try {
      const params = { brand_id: brandId, page };
      const res = await getAllProducts(token, params);

      if (res && res.status >= 200 && res.status < 300) {
        setProducts(res?.data);
        console.log("Products fetched successfully:", res.data);
      } else {
        Swal.fire(
          "Error",
          res?.data?.message || "Failed to fetch Products. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching Products:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  const fetchSizes = async () => {
    try {
      const res = await getAllSizes(token);
      if (res && res.status >= 200 && res.status < 300) {
        // console.log(res.data);
        setSizes(res.data);
      } else {
        Swal.fire("Error", "Failed to fetch sizes.", "error");
      }
    } catch (error) {
      console.error("Error fetching sizes:", error);
      Swal.fire("Error", "Network or server error occurred.", "error");
    }
  };

  useEffect(() => {
    fetchBrands();
    fetchSizes();
  }, []);

  const generateDesignID = () => {
    const designNoValue = designNo
      ? designNo?.value
        ? designNo.value
        : designNo
      : null;

    const designSizeValue = designSize?.label || null;

    const designId = `${acronym ? acronym : ""}-${
      designNoValue ? designNoValue : ""
    }-${designSizeValue ? designSizeValue : ""}`;
    setValue("designId", designId);
    // console.log(designId);

    return designId;
  };

  useEffect(() => {
    if (designNo && designSize && acronym) {
      fetchRateDiary(generateDesignID());
    }
  }, [designNo, designSize, acronym]);

  const brandOptions = useMemo(
    () => [
      ...brands.map((brand) => ({
        value: brand.brand_id,
        label: brand.brand_name,
        acronym: brand.acronym,
      })),
      { value: "addBrand", label: "Add Brand", isAddOption: true },
    ],
    [brands]
  );

  const customStyles = {
    option: (provided, state) => {
      if (state.data.isAddOption) {
        return {
          ...provided,
          backgroundColor: "#4baebc",
          fontWeight: "bold",
          color: "white",
          cursor: "pointer",
        };
      }
      return provided;
    },
  };

  const customFilterOption = (candidate, input) => {
    // console.log(candidate);

    if (candidate.data.isAddOption) {
      return true;
    }
    return candidate?.label?.toLowerCase().includes(input.toLowerCase());
  };

  const onSubmit = async (item) => {
    console.log("Submitting Rate Diary:", item);

    // if (!item.product || item.product.value === "addProduct") {
    //   Swal.fire("Error", "Please select a valid product.", "error");
    //   return;
    // }

    setIsLoading2(true);

    // Prepare request payload
    const data = {
      brand: item?.brand?.value,
      product: item?.product?.value,
      size: item?.designSize?.value,
      design_no: item?.designNumber?.value,
      stock: item?.stock,
      design_rate: item?.designRate,
      barcode_date: item?.barcodeDate,
      description: item?.note || null,
      is_update: false,
    };

    // console.log(data);

    try {
      const res = await addDesignRate(token, data);
      console.log("Rate Diary Response:", res);

      if (res && res.status >= 200 && res.status < 300) {
        Swal.fire({
          title: "Added!",
          text: "Rate Diary entry has been added successfully.",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          window.scrollTo(0, 0); // Scroll to top
          // reset();
          // setValue("brand", []);
          // setValue("product", []);
          setValue("designSize", []);
          // setValue("designNumber", null);
          setValue("designId", null);
          // setValue("acronym", "");
          setValue("stock", "");
          // setValue("barcodeDate", "");
          setValue("note", null);
          setRateDiary(null);
          setIsRateEditable(true);
        });
      } else if (res && res.status >= 400 && res.status < 500) {
        Swal.fire(
          "Error",
          res?.data?.message || "Invalid input. Please check your data.",
          "error"
        );
      } else {
        Swal.fire(
          "Error",
          res?.data?.error ||
            "An unexpected error occurred. Please try again later.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error during adding Rate Diary:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    } finally {
      setIsLoading2(false);
    }
  };

  const handleBrandChange = (selectedOption) => {
    // console.log(selectedOption);
    if (selectedOption?.value === "addBrand") {
      navigate("/brands/add");
    } else {
      reset();
      setRateDiary(null);
      setIsRateEditable(true);
      setValue("designNumber", []);
      setValue("note", null);
      setValue("designSize", null);
      setValue("product", null);
      setValue("acronym", selectedOption?.acronym || null);
      fetchProducts(selectedOption?.value);
      setValue("brand", selectedOption || null);
      clearErrors("brand");
    }
  };

  const handleProductChange = (selectedOption) => {
    setValue("designNumber", null);
    setValue("designSize", null);
    setValue("designRate", null);
    setValue("note", null);
    setValue("stock", null);
    setIsRateEditable(true);
    setRateDiary(null);

    if (selectedOption?.value === "addProduct") {
      navigate("/products/add");
    } else {
      setValue("product", selectedOption || null);

      // Extract design numbers directly from selectedOption
      const designNumbers =
        selectedOption?.product_design_number?.map(
          (design) => design.design_no
        ) || [];

      // Create design number options for dropdown
      const designNoOptions = designNumbers.map((designNo) => ({
        value: designNo,
        label: designNo,
      }));

      // console.log(designNoOptions);

      // Update design number dropdown options
      setDesignNoList(designNoOptions);

      if (designNoOptions.length === 1) {
        // If only one design number, select it automatically
        setValue("designNumber", designNoOptions[0]);
        clearErrors("designNumber");
      } else {
        // If multiple design numbers, reset design selection
        setValue("designNumber", null);
      }

      clearErrors("product");
    }
  };

  const handleDesignNoChange = (selectedOption) => {
    setValue("designSize", null);
    setValue("designRate", null);
    setValue("note", null);
    setValue("stock", null);
    setIsRateEditable(true);
    setRateDiary(null);

    setValue("designNumber", selectedOption || null);
    // console.log(selectedOption);
    clearErrors("designNumber");
  };

  const handleSiizeChange = (selectedOption) => {
    setValue("designRate", null);
    setValue("note", null);
    setValue("stock", null);
    setRateDiary(null);
    setIsRateEditable(true);

    setValue("designSize", selectedOption || null);
    clearErrors("designSize");
  };

  useEffect(() => {
    const defaultDate = generateBarcodeDate();
    setBarcodeDate(defaultDate);
    setValue("barcodeDate", defaultDate); // Sync with React Hook Form
  }, [setValue]);

  const handleBarcodeChange = (e) => {
    setBarcodeDate(e.target.value);
    setValue("barcodeDate", e.target.value); // Update form value
  };

  const generateBarcodeDate = () => {
    const monthCodes = {
      January: "JN",
      February: "FB",
      March: "MR",
      April: "AR",
      May: "MY",
      June: "JE",
      July: "JY",
      August: "AG",
      September: "SP",
      October: "OT",
      November: "NV",
      December: "DC",
    };

    const indianTime = new Date().toLocaleString("en-US", {
      timeZone: "Asia/Kolkata",
    });
    const currentDate = new Date(indianTime);

    const month = currentDate.toLocaleString("en-US", { month: "long" });
    const year = currentDate.getFullYear();

    return `2${monthCodes[month]}${year.toString().slice(-1)}`;
  };

  return (
    <div className="content-body">
      <div className="container-fluid">
        <div className="form-head d-flex mb-3 align-items-start justify-content-between">
          <div style={{ zIndex: 2 }}>
            <Link to="/rate-diary/list">
              <button className="btn btn-primary px-3 py-2 add-btn sticky-btn">
                Rate Diary List
              </button>
            </Link>
            <div className="m-4 p-1"></div>
          </div>
        </div>
        <div className="row">
          <div className="col-xl-12 col-lg-12">
            <div className="card">
              {/* <div className="card-header">
                <h4 className="card-title">Rate Diary Form</h4>
                <Link to="/rate-diary">
                  <img
                    src="/assets/images/kidskart/icon/back-button.png"
                    alt="Back"
                  />
                </Link>
              </div> */}
              <div className="card-body">
                <div className="basic-form">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row">
                      {/* Brand */}
                      <div className="mb-4 col-md-6">
                        <div className="d-flex">
                          <label className="form-label">
                            Company (Brand) Name
                          </label>
                          <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                        </div>
                        <Select
                          options={brandOptions}
                          {...register("brand", {
                            required: " Brand is required",
                          })}
                          onChange={handleBrandChange}
                          placeholder="Select Brand"
                          isClearable
                          value={watch("brand")}
                          styles={customStyles}
                          filterOption={customFilterOption}
                        />
                        {errors.brand && (
                          <p className="error">{errors.brand.message}</p>
                        )}
                      </div>

                      {/* Product Dropdown */}
                      <div className="mb-4 col-md-6">
                        <div className="d-flex">
                          <label className="form-label">Product</label>
                          <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                        </div>
                        <Select
                          options={
                            watch("brand")
                              ? [
                                  ...products?.map((product) => ({
                                    value: product.product_id,
                                    label: `${
                                      product.product_name
                                    } (${product.product_design_number
                                      ?.map((item) => item.design_no)
                                      .join(", ")})`,
                                    product_design_number:
                                      product.product_design_number,
                                  })),
                                  {
                                    value: "addProduct",
                                    label: "Add Product",
                                    isAddOption: true,
                                  }, // Add the "Add Product" option here
                                ]
                              : []
                          }
                          {...register("product", {
                            required: "Select a product",
                          })}
                          onChange={handleProductChange}
                          noOptionsMessage={() =>
                            watch("brand")
                              ? "No Product"
                              : "Please Select Brand"
                          }
                          placeholder="Search and select product / design number"
                          isClearable
                          value={watch("brand") ? watch("product") : []}
                          styles={customStyles}
                          filterOption={customFilterOption}
                        />
                        {errors.product && (
                          <p className="error">{errors.product.message}</p>
                        )}
                      </div>

                      <div className="mb-4 col-md-6">
                        <div className="d-flex">
                          <label className="form-label">Design Number</label>
                          <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                        </div>
                        <Select
                          options={watch("product") ? designNoList : []}
                          {...register("designNumber", {
                            required: "Select a design number",
                          })}
                          onChange={handleDesignNoChange}
                          noOptionsMessage={() => "Please Select Product"}
                          placeholder="Select design number"
                          isClearable
                          value={watch("brand") ? watch("designNumber") : []}
                        />
                        {errors.designNumber && (
                          <p className="error">{errors.designNumber.message}</p>
                        )}
                      </div>

                      {/* Acronym */}
                      <div className="mb-4 col-md-6">
                        <label className="form-label">Acronym</label>
                        <input
                          type="text"
                          className="form-control"
                          {...register("acronym", {
                            required: " Acronym is required",
                          })}
                          disabled
                        />
                      </div>

                      {/* Design Size */}
                      <div className="mb-4 col-md-6">
                        <div className="d-flex">
                          <label className="form-label">Design Size</label>
                          <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                        </div>
                        <Select
                          options={sizes.map((size) => ({
                            value: size.size_id,
                            label: size.size,
                          }))}
                          {...register("designSize", {
                            required: "Design size is required",
                          })}
                          onChange={handleSiizeChange}
                          placeholder="Select Size"
                          isClearable
                          value={watch("brand") ? watch("designSize") : []}
                        />
                        {errors.designSize && (
                          <p className="error">{errors.designSize.message}</p>
                        )}
                      </div>

                      {/* Design ID */}
                      <div className="mb-4 col-md-6">
                        <label className="form-label">Design ID</label>
                        <input
                          type="text"
                          {...register("designId")}
                          className="form-control"
                          value={generateDesignID()}
                          disabled
                        />
                      </div>

                      {/* Barcode Date */}
                      <div className="mb-4 col-md-6">
                        <div className="d-flex">
                          <label className="form-label">Barcode date</label>
                          <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter barcode date"
                          {...register("barcodeDate", {
                            required: "Barcode date is required",
                          })}
                          value={barcodeDate}
                          onChange={handleBarcodeChange} // Allows manual editing
                        />
                        {errors.barcodeDate && (
                          <p className="error">{errors.barcodeDate.message}</p>
                        )}
                      </div>

                      {/* Stock */}
                      <div className="mb-4 col-md-6">
                        <div className="d-flex">
                          <label className="form-label">Stock</label>
                          <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                        </div>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Enter stock quantity"
                          {...register("stock", {
                            required: "Stock is required",
                          })}
                        />
                        {errors.stock && (
                          <p className="error">{errors.stock.message}</p>
                        )}
                      </div>

                      {/* Design Rate */}
                      <div className="mb-4 col-md-6">
                        <div className="d-flex">
                          <label className="form-label">Design Rate</label>
                          <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                        </div>
                        <div
                          onClick={handleDesignRateFocus}
                          style={{
                            cursor: isRateEditable ? "auto" : "pointer",
                          }}
                        >
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Enter design rate"
                            {...register("designRate", {
                              required: "Design rate is required",
                            })}
                            disabled={!isRateEditable} // Disable input when "Keep Current" is selected
                            style={{
                              backgroundColor: !isRateEditable
                                ? "#f8f9fa"
                                : "white",
                              pointerEvents: isRateEditable ? "auto" : "none", // Allow input when editable
                            }}
                          />
                        </div>
                        {errors.designRate && (
                          <p className="error">{errors.designRate.message}</p>
                        )}
                      </div>

                      {/* Description */}
                      <div className="mb-4 col-md-6">
                        <label className="form-label">Note</label>
                        <textarea
                          type="text"
                          className="form-control"
                          placeholder="Write a note"
                          {...register("note")}
                          value={watch("note")}
                        />
                      </div>

                      {/* History */}
                      {rateDiary && rateDiary?.description?.length > 0 ? (
                        <div className="mb-4 col-md-6">
                          <label className="form-label fw-bold">Notes</label>
                          <div>
                            <table style={{ borderCollapse: "collapse" }}>
                              <thead>
                                <tr>
                                  <th style={{ border: "none" }}></th>
                                  <th style={{ border: "none" }}></th>
                                  <th style={{ border: "none" }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {rateDiary?.description?.map((item, i) => (
                                  <tr key={i}>
                                    <td style={{ border: "none" }}>
                                      {new Date(item.date)
                                        .toLocaleDateString("en-GB", {
                                          day: "2-digit",
                                          month: "long",
                                          year: "numeric",
                                        })
                                        .replace(" ", " ")
                                        .replace(/(\w+ \w+)/, "$1,")}
                                    </td>
                                    <td className="px-3 py-2">:</td>
                                    <td style={{ border: "none" }}>
                                      {item.content}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    {!isLoading2 ? (
                      <div className="col-sm-4">
                        <button type="submit" className="btn btn-primary w-100">
                          ADD ENTRY
                        </button>
                      </div>
                    ) : (
                      <div className="col-sm-4">
                        <button
                          type="button"
                          className="btn btn-primary w-100"
                          disabled
                        >
                          ADDING DIARY
                        </button>
                        <div
                          className="spinner-border2 text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
